import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  canViewAIgeneratedReports,
  downloadAIgeneratedReport,
  downloadInterpretedReport,
  downloadPrescriptionFile,
  getAIReportPdfUrl,
} from "./reportUtils";
import {
  ANALYTICS_EVENTS,
  REPORT_TYPE,
  logAnalyticsEvent,
} from "../../../analytics";

const TEXT_BUTTON_SX = {
  color: "#5751E9",
  fontSize: 16,
  fontWeight: 700,
  textTransform: "none",
  px: 0,
  minWidth: 0,
  backgroundColor: "transparent",
  textDecoration: "underline",
  "&:hover": {
    bgcolor: "transparent",
  },
};

const buildFooterButtonSx = () => ({
  minWidth: "155px",
  width: { xs: "100%", sm: "auto" },
  height: "48px",
  borderRadius: "9px",
  padding: "14px 12px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  fontFamily: "Figtree",
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "100%",
  letterSpacing: "0%",
  textTransform: "none",
  color: "#5751E9",
  backgroundColor: "transparent",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "rgba(87, 81, 233, 0.08)",
    boxShadow: "none",
  },
  "&:focus": {
    outline: "none",
    boxShadow: "none",
  },
});

const DownloadMenuItem = ({ label, onClick }) => (
  <MenuItem
    onClick={onClick}
    sx={{
      height: 44,
      "&:hover": { backgroundColor: "var(--app-surface-2)" },
    }}
  >
    <ListItemIcon sx={{ minWidth: 32, color: "#6F6F6F" }}>
      <Icon icon="material-symbols:download" width="18" height="18" />
    </ListItemIcon>
    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: 14,
        fontWeight: 500,
        color: "#3C3C3C",
      }}
    />
  </MenuItem>
);

const DownloadDivider = () => (
  <Divider
    sx={{
      mx: 1.5,
      width: "calc(100% - 24px)",
      borderColor: "var(--app-text)",
    }}
  />
);

const ReportDownloadActions = ({
  data,
  ecgGraph,
  isLoadingGraph,
  mode = "short",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const isCompleted = data?.interpretation_request_status === "completed";
  const aiReportUrl = getAIReportPdfUrl(ecgGraph) || getAIReportPdfUrl(data);
  const showAIReport = canViewAIgeneratedReports() && Boolean(aiReportUrl);
  const hasInterpretedReport = Boolean(
    ecgGraph?.ReportPdfURL || ecgGraph?.ReportPdf,
  );
  const hasPrescription = Boolean(ecgGraph?.prescriptionUrl);
  const isFooter = mode === "footer";
  const footerButtonSx = buildFooterButtonSx();

  const handleDownloadClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Same id resolution the ECG reports screen uses, so report_id joins across
  // both download surfaces instead of being null on one of them.
  const reportId = data?.report?.id || data?.id;

  const downloadAndTrack = (reportType, download) => () => {
    logAnalyticsEvent(ANALYTICS_EVENTS.REPORT_DOWNLOADED, {
      report_type: reportType,
      report_id: reportId,
    });
    download();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (isLoadingGraph) {
    return isFooter ? (
      <Skeleton
        variant="rounded"
        width={155}
        height={48}
        animation="wave"
        sx={{ borderRadius: "9px" }}
      />
    ) : (
      <CircularProgress size={24} />
    );
  }

  // Everything the user is allowed to download for this report, in menu order.
  // The interpreted report and prescription only exist once interpretation is
  // complete; the AI report is offered either way, when permitted.
  const options = [
    isCompleted &&
      hasInterpretedReport && {
        label: "Download Interpreted Report",
        onClick: downloadAndTrack(REPORT_TYPE.ECG_REPORT, () =>
          downloadInterpretedReport(ecgGraph),
        ),
      },
    showAIReport && {
      label: "Download AI Generated Report",
      onClick: downloadAndTrack(REPORT_TYPE.AI_REPORT, () =>
        downloadAIgeneratedReport(ecgGraph || data),
      ),
    },
    isCompleted &&
      hasPrescription && {
        label: "Download Prescription",
        onClick: downloadAndTrack(REPORT_TYPE.PRESCRIPTION, () =>
          downloadPrescriptionFile(ecgGraph?.prescriptionUrl),
        ),
      },
  ].filter(Boolean);

  if (options.length === 0) return null;

  // A dropdown holding one row is just a second click — go straight to it.
  if (options.length === 1) {
    const [only] = options;

    return (
      <Button
        className={isFooter ? "btn btn-added-primary2" : undefined}
        variant="text"
        size="small"
        onClick={only.onClick}
        sx={isFooter ? { ...footerButtonSx, minWidth: "240px" } : TEXT_BUTTON_SX}
        startIcon={
          <Icon icon="material-symbols:download" width="18" height="18" />
        }
      >
        {only.label}
      </Button>
    );
  }

  return (
    <>
      <Button
        className={isFooter ? "btn btn-added-primary2" : undefined}
        onClick={handleDownloadClick}
        startIcon={
          menuOpen ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          )
        }
        sx={isFooter ? footerButtonSx : TEXT_BUTTON_SX}
      >
        Download
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: isFooter ? "top" : "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: isFooter ? "bottom" : "top",
          horizontal: "left",
        }}
        MenuListProps={{ disablePadding: true }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            minWidth: 235,
            boxShadow:
              "0px 12px 32px rgba(0, 0, 0, 0.16), 0px 4px 10px rgba(0,0,0,0.06)",
            backgroundColor: "var(--app-surface)",
            p: 0,
            mt: isFooter ? "-12px" : 0,
            "& .MuiMenu-list": { p: 0 },
          },
        }}
      >
        {/* Flat array (not Fragments) so MUI's MenuList still sees each row */}
        {options.flatMap((option, index) => [
          ...(index > 0
            ? [<DownloadDivider key={`${option.label}-divider`} />]
            : []),
          <DownloadMenuItem
            key={option.label}
            label={option.label}
            onClick={() => {
              option.onClick();
              handleMenuClose();
            }}
          />,
        ])}
      </Menu>
    </>
  );
};

export default ReportDownloadActions;
